// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img
import HeroImage from 'public/img/technology/kroemtech_technology.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody } from '@/assets/theme/fonts'

/**
 * The wash laid over the hero photo, in the same off-white the alternating
 * section bands use, so the hero and the section below it agree.
 */
const wash = (alpha: number) =>
  `rgba(${themeColors.section.lightRgb}, ${alpha})`

export default function HeroSection() {
  const { t } = useTranslation('technology')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const isDesktop = w > breakpoints.desktopToMobile
  const isTablet = w > breakpoints.mobileLarge && !isDesktop

  const getOverlay = () => {
    if (isDesktop)
      return `linear-gradient(to right, ${wash(0.97)} 0%, ${wash(0.93)} 32%, ${wash(0.6)} 62%, ${wash(0.3)} 100%)`
    if (isTablet)
      return `linear-gradient(to right, ${wash(0.97)} 0%, ${wash(0.9)} 55%, ${wash(0.6)} 100%)`
    return `linear-gradient(to bottom, ${wash(0.95)} 0%, ${wash(0.92)} 60%, ${wash(0.85)} 100%)`
  }

  const getMinHeight = () => {
    if (w > breakpoints.desktopSmall) return '40rem'
    if (isDesktop) return '36rem'
    if (isTablet) return '32rem'
    return 'auto'
  }

  const getTextWidth = () => {
    if (w > breakpoints.desktopSmall) return '54%'
    if (isDesktop) return '64%'
    if (isTablet) return '80%'
    return '100%'
  }

  const getTitleFont = () => {
    if (w > breakpoints.desktopSmall) return '3.5rem'
    if (isDesktop) return '3rem'
    if (isTablet) return '2.5rem'
    if (w > breakpoints.mobileSmall) return '2.125rem'
    return '1.75rem'
  }

  const getSubtitleFont = () => {
    if (isDesktop) return '1.6rem'
    if (isTablet) return '1.35rem'
    return '1.15rem'
  }

  const getContent = () => (
    <Box sx={{ width: '100%', paddingTop: '110px' }}>
      <Box sx={{ width: getTextWidth() }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: getTitleFont(),
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: themeColors.gray.dark,
            marginBottom: '1.25rem'
          }}
        >
          {t('hero.title')}
        </Typography>

        <Typography
          component="p"
          sx={{
            fontSize: getSubtitleFont(),
            fontWeight: 600,
            lineHeight: 1.3,
            color: themeColors.bg.start,
            marginBottom: '1.5rem'
          }}
        >
          {t('hero.subtitle')}
        </Typography>

        {/* The two-line contrast that sets up the whole page. */}
        <Box
          sx={{
            paddingLeft: '1rem',
            borderLeft: `3px solid ${themeColors.success.main}`,
            marginBottom: '1.75rem'
          }}
        >
          {[t('hero.lead1'), t('hero.lead2')].map(line => (
            <Typography
              key={line}
              component="p"
              sx={{
                fontSize: getFontBody(),
                fontWeight: 500,
                lineHeight: 1.6,
                color: themeColors.gray.dark
              }}
            >
              {line}
            </Typography>
          ))}
        </Box>

        {[t('hero.p1'), t('hero.p2'), t('hero.p3'), t('hero.p4')].map(
          paragraph => (
            <Typography
              key={paragraph}
              component="p"
              sx={{
                fontSize: getFontBody(),
                lineHeight: 1.7,
                color: themeColors.gray.defaultText,
                marginBottom: '0.9rem',
                maxWidth: '36rem',
                '&:last-of-type': { marginBottom: 0 }
              }}
            >
              {paragraph}
            </Typography>
          )
        )}
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: getMinHeight(),
        display: 'flex',
        alignItems: 'center',
        backgroundImage: `url(${HeroImage.src})`,
        backgroundSize: 'cover',
        backgroundPosition: isDesktop ? 'center' : 'right center',
        backgroundRepeat: 'no-repeat',
        paddingBottom: isDesktop ? '3rem' : '2.5rem'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: getOverlay(),
          pointerEvents: 'none'
        }}
      />

      <Box sx={{ position: 'relative', width: '100%', zIndex: 1 }}>
        <Section
          justifyContent="left"
          background="transparent"
          content={getContent()}
        />
      </Box>
    </Box>
  )
}
